import { HTMLInputTypeAttribute, memo } from 'react';
import { FieldError, UseFormRegister } from 'react-hook-form';
import { isEqual } from 'lodash';
import styled, { css } from 'styled-components';
import cx from 'classnames';

import { Fields, Names } from 'types/fields';
import Text from '../text';
import { device } from 'styles/breakpoints';
import StyledTypography from '../typography/styles';

export type TextFieldProps = {
  type: HTMLInputTypeAttribute;
  label?: string;
  name: Names;
  register: UseFormRegister<Fields>;
  error?: FieldError;
  placeholder?: string;
  initialValue?: string;
};

function TextField({
  type,
  label,
  name,
  register,
  error,
  placeholder,
  initialValue,
}: TextFieldProps) {
  return (
    <TextFieldWrapper data-testid="text-main-wrapper">
      {label && (
        <TextFieldLabel data-testid="text-field-label">
          <label htmlFor={name}>
            <Text type="mode2" fw="light">
              {label}
            </Text>
          </label>
        </TextFieldLabel>
      )}

      <TextFieldInput data-testid="text-field-wrapper">
        <input
          defaultValue={initialValue}
          type={type}
          id={name}
          data-testid="text-field"
          placeholder={placeholder}
          {...register(name)}
          className={cx({ invalid: Boolean(error) })}
        />
      </TextFieldInput>

      <TextFieldError data-testid="text-field-error">
        {error?.message && (
          <Text type="mode2" fw="medium">
            {error.message}
          </Text>
        )}
      </TextFieldError>
    </TextFieldWrapper>
  );
}

type VariantType = {
  inputVariant?: 'outlined' | 'underlined';
};

export const TextFieldWrapper = styled.div<VariantType>`
  &:not(:first-child) {
    margin-top: 24px;
  }

  @media ${device.tablet} {
    &:not(:first-child) {
      margin-top: 20px;
    }
  }

  ${({ inputVariant }) =>
    isEqual(inputVariant, 'underlined') &&
    css`
      display: flex;
    `}
`;

export const TextFieldLabel = styled.div<{
  alignLeft?: boolean;
  inputVariant?: 'outlined' | 'underlined';
  disabled?: boolean;
}>`
  display: flex;
  margin-bottom: 8px;

  ${({ inputVariant }) =>
    isEqual(inputVariant, 'underlined') &&
    css`
      color: ${({ theme }) => theme.colors.grayPallet9};
      margin-bottom: 0;
      align-items: end;

      & > label > p {
        font-size: 20px;
        font-weight: 500;
        line-height: 1 !important;

        @media ${device.tablet} {
          font-size: 17px !important;
        }
      }
    `}

  ${({ inputVariant, disabled }) =>
    isEqual(inputVariant, 'underlined') &&
    disabled &&
    css`
      > label {
        cursor: not-allowed;
        > p {
          color: ${({ theme }) => theme.colors.grayPallet18} !important;
        }
      }
    `}

  > label {
    cursor: pointer;
    > p {
      color: ${({ theme }) => theme.colors.blackPallet6};
      ${({ alignLeft }) => (alignLeft ? 'text-align: left' : '')}
    }
  }

  ${({ disabled }) =>
    disabled &&
    css`
      > label {
        cursor: not-allowed;
        > p {
          color: ${({ theme }) => theme.colors.grayPallet18};
        }
      }
    `}
`;

export const TextFieldInput = styled.div<{
  inputVariant?: 'outlined' | 'underlined';
  isTransparentBackground?: boolean;
}>`
  > input {
    height: 55px;
    width: 100%;
    border: 1px solid ${({ theme }) => theme.colors.grayPallet19};
    background-color: ${({ theme }) => theme.colors.whitePallet3};
    padding: 16px 20px;
    font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
    color: ${({ theme }) => theme.colors.blackPallet6};
    font-size: 17px;
    line-height: 23px;
    transition: 200ms all ease;

    @media ${device.tablet} {
      font-size: 16px;
    }

    &::placeholder {
      color: ${({ theme }) => theme.colors.grayPallet18};
    }

    &:hover {
      border-color: hsl(0, 0%, 70%);
    }
    &:focus {
      border: 1px solid ${({ theme }) => theme.colors.green};
    }
    &.invalid {
      border: 1px solid ${({ theme }) => theme.colors.redPallet3};
    }
    &:disabled,
    &:disabled:hover {
      border: 1px solid ${({ theme }) => theme.colors.grayPallet20};
      color: ${({ theme }) => theme.colors.grayPallet30};
      background-color: ${({ theme }) => theme.colors.grayPallet20};
    }
  }

  ${({ isTransparentBackground }) =>
    isTransparentBackground &&
    css`
      > input {
        background-color: transparent;
      }
    `}

  ${({ inputVariant }) =>
    isEqual(inputVariant, 'underlined') &&
    css`
      & > input {
        height: auto;
        border: none;
        border-bottom: 1px solid ${({ theme }) => theme.colors.black};
        background-color: transparent;
        padding: 0 10px;
        margin-left: 7px;

        @media ${device.tablet} {
          padding: 0 5px;
        }

        &:hover {
          border: none;
          border-bottom: 1px solid ${({ theme }) => theme.colors.black};
        }
        &:focus {
          border: none;
          border-bottom: 1px solid ${({ theme }) => theme.colors.green};
        }
        &.invalid {
          border: none;
          border-bottom: 1px solid ${({ theme }) => theme.colors.redPallet3};
        }
        &:disabled,
        &:disabled:hover {
          border: none;
          border-bottom: 1px solid ${({ theme }) => theme.colors.grayPallet18};
          color: ${({ theme }) => theme.colors.grayPallet18};
          background-color: transparent;
        }
      }
    `}

  ${StyledTypography.Typography} {
    margin-top: 5px;
    display: inline-block;
    text-align: left;
  }
`;

export const TextFieldError = styled.div`
  > p {
    color: ${({ theme }) => theme.colors.red};
  }

  & > span {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.red};
  }
`;

export default memo(TextField);
